import {AfterViewChecked, ChangeDetectorRef, Component, ElementRef, Input, OnInit, Renderer2, ViewChild} from '@angular/core';
import {VRGroupService} from '../../vrgroup.service';
import {_, parseToDate} from '../../../../shared/utils';
import {compareAsc, differenceInMinutes, format} from 'date-fns/esm';
import {BaseComponent} from '../../../../components/general/base/base.component';
import { ScheduleTrain, TrainTimetableEntry } from '../../common/train';
import {StationTrainRow} from '../../common/station';
import { InfoActionService } from '../../../../element-utils/element-actions/info-action/info-action.service';
import { AlarmMessage } from '../../../../alarm/alarm-message';
import { SidebarService } from '../../../../shared/sidebar/sidebar.service';
import { TabId } from '../../train-info/train-info/train-info.component';

type StationTrainRowForTemplate = StationTrainRow & {
  scrollToAfterLoad?: true
  alarms: AlarmMessage[]
};

@Component({
  selector: 'qm-station-table',
  templateUrl: './station-table.component.html',
  styleUrls: ['./station-table.component.scss']
})
export class StationTableComponent extends BaseComponent implements OnInit, AfterViewChecked {
  @Input()
  public station: any;
  public timetableDate = new Date();
  public trains: StationTrainRowForTemplate[];
  public scroll = false;
  public filters = new Map<string, { name: string, active: boolean }>();
  @ViewChild('trainTableRows')
  templateTrainTableRows: ElementRef;
  /**
   * Default constructor, inject dependencies.
   * @param {VRGroupService} vrGroupService
   * @param {ChangeDetectorRef} changeDetector
   */
  constructor(
    private vrGroupService: VRGroupService,
    private infoActionService: InfoActionService,
    private changeDetector: ChangeDetectorRef,
    private sidebarService: SidebarService
  ) {
    super();
    this.name = 'StationTable';
  }

  ngOnInit() {
    console.log('StationTableComponent init', this.station);
    this.filters.set('Arrivals', {
      name: _('Arrivals'),
      active: true
    });
    this.filters.set('Departures', {
      name: _('Departures'),
      active: false
    });
    this.filters.set('COMMUTER', {
      name: _('Commuter'),
      active: true
    });
    this.filters.set('LONGDISTANCE', {
      name: _('Long-distance'),
      active: true
    });
    this.filters.set('FREIGHT', {
      name: _('Freight train'),
      active: true
    });
    this.filters.set('LOCOMOTIVE', {
      name: _('Locomotive'),
      active: true
    });
    this.filters.set('On-track machines', {
      name: _('On-track machines'),
      active: true
    });
    this.filters.set('Shunting', {
      name: _('Shunting'),
      active: true
    });
    this.updateStation().then(() => this.findNextArrivingTrain());
  }

  ngAfterViewChecked() {
    if (this.scroll) {
      this.scrollToLatest();
    }
  }

  private findNextArrivingTrain() {
    if(!this.trains) {
      return;
    }
    const now = new Date();
    const arrivingTrains = this.trains.filter(value => !!value?.arrival?.schedule);

    const latestTrain = arrivingTrains.find((value, index, obj) => {
      const isLastTimetableRow = index === obj.length - 1;
      // no arriving trains scheduled after current time, scrolling to bottom
      if(isLastTimetableRow) {
        return true;
      }

      const diff = (val: Date) => val.getTime() - now.getTime();
      return diff(parseToDate(value.arrival.schedule)) > 0;
    });

    latestTrain.scrollToAfterLoad = true;
    this.scroll = true;
  }

  private scrollToLatest() {
    const el = this.templateTrainTableRows.nativeElement.getElementsByClassName('latest').item(0);
    if (el && (el.getBoundingClientRect() as DOMRect).x !== 0) {
      el.scrollIntoView({
        // TODO: otherwise the default will not show the row in view for some reason
        // maybe it's due to the first row being hidden by the headers?
        block: 'center'
      });
      this.scroll = false;
    }
  }

  /**
   * Stop is visible if stop type !== pass or show all is on
   * TODO Make own train type for this!
   * @param train
   * @returns {boolean}
   */
  public trainVisible(train: ScheduleTrain) {
    if (this.filters.has(train.type)) {
      return this.filters.get(train.type).active;
    } else {
      return true;
    }
  }

  /**
   * Return row styling for conflicts with other trains.
   * @param row - current row
   * @param index - current row index in station list
   * @returns {string} -  class name for current row
   */
  private getRowClass(row: StationTrainRowForTemplate, index: number): string {
    if (row.cancelled) {
      return 'cancelled';
    } else if (row.arrival && row.arrival.actual) {
      const diff = differenceInMinutes(parseToDate(row.arrival.actual), parseToDate(row.arrival.schedule));
      if (diff <= 5) {
        return 'text-success';
      } else if (diff <= 15) {
        return 'text-warning';
      } else {
        return 'text-danger';
      }
    } else {
      return '';
    }
  }

  public getRowClasses(row: StationTrainRowForTemplate, index: number): string {
    return [
      this.getRowClass(row, index),
      row.scrollToAfterLoad ? 'latest': ''
    ].join(' ');
  }

  public showArrivals(): boolean {
    return (this.filters.get('Arrivals').active);
  }

  public showDepartures(): boolean {
    return (this.filters.get('Departures').active);
  }

  public async updateStation() {
    console.log('StationTableComponent update');
    this.loading = null;
    const short = this.station.short.toUpperCase();
    await this.getCurrentStationAsTimetableRows(short);
    this.changeDetector.markForCheck();
  }

  public getCause(row: TrainTimetableEntry): string {
    try {
      return this.vrGroupService.getTextForLiviStatus(row.causes);
    } catch (error) {
      return '';
    }
  }

  private async getCurrentStationAsTimetableRows(station: string): Promise<void> {
    this.loading = await this.vrGroupService.getTrainsForStation(station, format(this.timetableDate, '"yyyy-MM-dd"'));
    if (!this.loading.isOk) {
      return;
    }
    this.trains = (this.loading.data.data).map(train => {
      const retVal: StationTrainRow = new StationTrainRow();
      const stations = train.timetable.filter(o => {
        return o.station === station;
      });
      retVal.causes = stations[0].causes ? stations[0].causes : null;
      retVal.commercial = stations[0].commercial;
      retVal.stop = stations[0].stop;
      retVal.cancelled = stations[0].cancelled;
      retVal.track = stations[0].track;
      if (stations[0].type === 'ARRIVAL') {
        retVal.arrival = stations[0];
        retVal.departure = stations.length === 2 ? stations[1] : null;
      } else if (stations[0].type === 'DEPARTURE') {
        retVal.departure = stations[0];
        retVal.arrival = stations.length === 2 ? stations[1] : stations[0];
      }

      retVal.train = train;
      return retVal;
    }).sort((a, b) => {
      return compareAsc(parseToDate(a.arrival.schedule), parseToDate(b.arrival.schedule));
    });
  }

  public async onTrainIdClicked(trainRow: StationTrainRow): Promise<void> {
    const el = await this.vrGroupService.getTrainMapElement(trainRow.train.tripNumber, trainRow.train.tripDate);
    this.infoActionService.openDetails(el.layer, el);
  }
  public async openDetails(trainRow: StationTrainRow, tabId: TabId) {
    console.warn(trainRow);
    const feature = await this.vrGroupService.getTrainMapElement(trainRow.train.tripNumber, trainRow.train.tripDate);
    this.sidebarService.requestSidebarPanel('ElementInfo' + trainRow.train._id, { openTab: tabId, feature });
  }
}
